import React, { useCallback, useEffect, useState } from 'react';

import { Group, Text, useMantineTheme } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconEdit, IconTrashX } from '@tabler/icons-react';
import Menu from 'components/Menu';
import Table from 'components/Table';
import useNotificationMessageTemplates from 'hooks/notificationMessageTemplate/useNotificationTemplateMessages';
import { CellProps } from 'react-table';
import { NotificationMessageTemplate } from 'types';

import ModalNotificationMessageTemplateDelete from './ModalNotificationMessageTemplateDelete';

interface INotificationMessageTemplatesTableContainerProps {
  permission?: boolean;
  tableData: NotificationMessageTemplate[];
  setTableData: React.Dispatch<React.SetStateAction<NotificationMessageTemplate[]>>;
  setSelectedNotificationMessageTemplate: (notificationMessageTemplate: NotificationMessageTemplate) => void;
  selectedNotificationMessageTemplate: NotificationMessageTemplate | null;
  handleDrawerToggle: (notificationType: 'SMS' | 'Email') => void;
}

const NotificationMessageTemplatesTableContainer = ({
  tableData,
  setTableData,
  selectedNotificationMessageTemplate,
  setSelectedNotificationMessageTemplate,
  handleDrawerToggle,
  permission = true,
}: INotificationMessageTemplatesTableContainerProps): JSX.Element => {
  const theme = useMantineTheme();
  const [ordering, setOrdering] = useState('description');
  const [ascending, setAscending] = useState(true);

  const { data, fetchNextPage, hasNextPage, isSuccess, isError, isLoading, isFetchingNextPage } =
    useNotificationMessageTemplates({
      ordering: ascending ? ordering : `-${ordering}`,
    });

  const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);

  const handleHeaderClick = useCallback(
    (header: string): void => {
      setOrdering(header);
      setAscending(!ascending);
    },
    [ascending],
  );

  const handleEdit = (notificationMessageTemplate: NotificationMessageTemplate): void => {
    setSelectedNotificationMessageTemplate(notificationMessageTemplate);
    handleDrawerToggle(notificationMessageTemplate.notificationType);
  };

  const handleDelete = (notificationMessageTemplate: NotificationMessageTemplate): void => {
    setSelectedNotificationMessageTemplate(notificationMessageTemplate);
    openDeleteModal();
  };

  const columns = [
    {
      Header: 'Description',
      accessor: 'description' as const,
      Cell: ({ row }: CellProps<NotificationMessageTemplate>) => (
        <Text
          onClick={() => handleEdit(row.original)}
          weight='bold'
          underline
          sx={{ cursor: 'pointer', width: 'fit-content' }}
        >
          {row.original.description}
        </Text>
      ),
    },
    {
      Header: 'Notification Type',
      accessor: 'notificationType' as const,
    },
    {
      id: 'action',
      Cell: ({ row }: CellProps<NotificationMessageTemplate>) => (
        <Group position='right' px='xs'>
          <Menu
            position='bottom-end'
            disabled={!permission}
            items={[
              {
                title: 'Edit',
                onClick: () => handleEdit(row.original),
                icon: <IconEdit size={theme.fontSizes.sm} />,
              },
              {
                title: 'Delete',
                onClick: () => handleDelete(row.original),
                icon: <IconTrashX size={theme.fontSizes.sm} />,
                color: 'red',
              },
            ]}
          />
        </Group>
      ),
      minWidth: '50px',
      maxWidth: '50px',
      sticky: true,
    },
  ];

  useEffect(() => {
    if (!isLoading && data) {
      setTableData(data.pages.map(page => page.data.results).flat());
    }
  }, [isLoading, data, setTableData]);

  return (
    <>
      <Table
        tableData={tableData}
        columns={columns}
        isSuccess={isSuccess}
        isLoading={isLoading}
        isError={isError}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={!!hasNextPage}
        fetchNextPage={fetchNextPage}
        clickableHeaders={['description', 'notificationType']}
        handleHeaderClick={handleHeaderClick}
        ordering={ordering}
        ascending={ascending}
      />

      {selectedNotificationMessageTemplate && (
        <ModalNotificationMessageTemplateDelete
          opened={!!selectedNotificationMessageTemplate && deleteModalOpened}
          handleClose={closeDeleteModal}
          notificationMessageTemplate={selectedNotificationMessageTemplate}
        />
      )}
    </>
  );
};

export default NotificationMessageTemplatesTableContainer;
